import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoSubSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";

const SITE = "https://www.regard-de-nature.fr";

const NaturopatheStress = () => {
  useSEO({
    title: "Naturopathie & gestion du stress – Regard de Nature",
    description:
      "Réduire le stress naturellement grâce à la naturopathie : alimentation, plantes, respiration, hygiène de vie. Accompagnement personnalisé par Laura Elie.",
    canonical: `${SITE}/naturopathe-stress`,
  });

  return (
    <SeoPageLayout
      h1="Réduire le stress naturellement avec la naturopathie"
      intro="Mâchoire serrée, mental qui tourne en boucle, sensation de ne jamais pouvoir vraiment se poser ? Vous n'êtes pas seul·e — et il existe des leviers concrets pour relâcher la pression, pas à pas."
    >
      <SeoSection title="Le stress, un signal à écouter">
        <p>
          Je reçois souvent des personnes qui vivent en mode « tendu » depuis des mois, parfois sans même s'en
          rendre compte : épaules nouées, sommeil léger, irritabilité, ventre serré au réveil. Le stress fait
          partie de la vie. Il devient problématique lorsqu'il s'installe dans la durée et grignote notre énergie,
          notre sommeil, notre digestion ou notre humeur.
        </p>
        <p>
          La naturopathie n'a pas vocation à supprimer le stress — c'est un mécanisme naturel utile — mais à vous
          aider à mieux le réguler, à reconstituer vos ressources et à reprendre la main sur votre quotidien.
        </p>
      </SeoSection>

      <SeoSection title="Ce que vous pouvez retrouver">
        <SeoList
          items={[
            "Une capacité à relâcher en fin de journée, sans avoir besoin d'un verre ou d'un écran.",
            "Un sommeil moins haché, qui récupère vraiment.",
            "Un mental plus posé, moins envahi par les ruminations.",
            "Une énergie plus stable, sans yo-yo de stimulants.",
            "Le sentiment de reprendre la main sur votre rythme.",
          ]}
        />
      </SeoSection>

      <SeoSection title="Comment je vous accompagne">
        <p>
          Dans ma pratique, l'accompagnement est global. Nous explorons ensemble les différentes dimensions de
          votre vie pour identifier ce qui pèse, ce qui ressource, ce qui peut être ajusté. Il ne s'agit pas d'un
          traitement, mais d'une démarche éducative et préventive, construite à partir de votre quotidien réel.
        </p>
        <SeoSubSection title="Les leviers que nous travaillons">
          <SeoList
            items={[
              "L'alimentation : équilibre glycémique, micronutrition, magnésium, oméga-3",
              "Le sommeil : ritualisation du coucher, gestion des écrans, respiration",
              "L'activité physique : adaptée à votre énergie et à votre rythme",
              "La respiration et la cohérence cardiaque",
              "Les plantes et élixirs floraux : soutien naturel selon votre profil",
              "L'environnement : qualité de l'air, du repos, des liens",
            ]}
          />
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Les fleurs de Bach, un soutien émotionnel précieux">
        <p>
          Lorsque le stress s'accompagne d'émotions difficiles à gérer (anxiété, ruminations, irritabilité,
          découragement), je propose souvent les{" "}
          <Link to="/fleurs-de-bach" className="text-primary underline hover:text-secondary">élixirs floraux du Dr Bach</Link>{" "}
          comme soutien doux et personnalisé. Ce ne sont pas des médicaments, mais une approche subtile pour
          accompagner les états émotionnels au fil des semaines.
        </p>
      </SeoSection>

      <SeoSection title="Ce qui guide ma pratique">
        <p>
          Je vous propose des actions simples, réalistes, qui s'intègrent à votre quotidien sans le bouleverser.
          L'idée n'est pas d'ajouter une contrainte de plus, mais de retrouver des espaces de respiration, de
          plaisir et de sens. J'observe que c'est dans cette progressivité que le système nerveux se rééduque
          vraiment.
        </p>
        <p className="italic text-foreground/70">
          Important : si votre stress devient envahissant ou s'accompagne de symptômes importants, un avis médical
          ou un suivi psychologique reste indispensable. La naturopathie s'inscrit en complément.
        </p>
      </SeoSection>

      <SeoSection title="Pour qui ?">
        <SeoList
          items={[
            "Adultes confrontés au stress professionnel ou personnel",
            "Parents épuisés, en charge mentale chronique",
            "Personnes en période de transition de vie",
            "Étudiants ou jeunes actifs en surcharge",
            "Toute personne souhaitant prévenir l'épuisement",
          ]}
        />
        <p className="mt-4">
          Je consulte à{" "}
          <Link to="/naturopathe-montesquieu-lauragais" className="text-primary underline hover:text-secondary">
            Montesquieu-Lauragais
          </Link>{" "}
          et à{" "}
          <Link to="/naturopathe-villefranche-de-lauragais" className="text-primary underline hover:text-secondary">
            Villefranche-de-Lauragais
          </Link>
          , dans le Lauragais.
        </p>
      </SeoSection>

      {/* CTA fin de page */}
      <section className="mt-10 sm:mt-12 p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-secondary/5 border border-border rounded-lg text-center">
        <h2 className="text-xl sm:text-2xl font-cinzel font-bold text-primary mb-3">
          Faire le point et relâcher la pression, à votre rythme
        </h2>
        <div className="w-12 h-px bg-secondary mx-auto mb-4" />
        <p className="text-base text-foreground/80 font-montserrat mb-5 max-w-2xl mx-auto">
          Prendre rendez-vous, c'est poser un premier pas concret pour soulager le système nerveux et retrouver de
          la marge.
        </p>
        <Link to="/#accompagnements">
          <Button size="lg" className="bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 px-6 font-montserrat">
            <Calendar className="w-5 h-5 mr-2" />
            Commencer un accompagnement adapté
          </Button>
        </Link>
      </section>
    </SeoPageLayout>
  );
};

export default NaturopatheStress;
